import { logger } from '@adornis/base/logging.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { readFile } from 'node:fs/promises';
import sharp from 'sharp';
import { extractImageFromPDF } from '../../utils/transform-file.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { addCostToExam } from '../add-cost.js';
import { resolveFileIdsToFiles } from '../file-intake.js';
import { type Box, type LocatedNames, parseNameBoxes } from './name-boxes.js';

/**
 * Anonymizes a batch of submission PDFs and images BEFORE the split agent (`split-file-agent.ts`) ever sees them. This is
 * PERMANENT, not a transient step undone afterward: the redacted PAGES this module produces (persisted as
 * `ImageFile`s, keyed by each input file's own ORIGINAL id — see `redactedImageIds`) are the ONLY version
 * anything downstream ever touches — the split model's own analysis, the final `Submission.imageIds`, and
 * whatever the teacher-facing UI displays are all the SAME blackened-and-labeled pages. The real, unredacted
 * image is seen by exactly one thing in this whole pipeline — the name-locate model call in phase A below —
 * and is never read again after that call returns. The whole point is that a student's real name never
 * reaches an AI model outside our own infrastructure (today: OpenRouter; qwen is planned to move in-house),
 * and keeping only ONE persisted copy of each page (never an original+redacted pair to keep in sync) is what
 * makes that guarantee hold structurally rather than by every future caller remembering to pick the right one.
 * The redacted box itself carries NOTHING visible any more — a plain black rect, no stamped label. The real
 * name still reaches where it's needed — `Submission.studentName`, set via match-back in `split-file-agent.ts`
 * — purely through `nameMapping`'s own per-PAGE `transcribedName`, carried as DATA ALONGSIDE the images, never
 * drawn onto one and never sent to any model a second time: `split-file-agent.ts` reads this real name itself
 * only on our own side, AFTER the split model has already decided the grouping, to assign `student_name`. The
 * split model's own prompt never sees this string at all — see that file's own `buildAnonymizedNameLabels` for
 * what it gets instead, and why.
 *
 * NO REDACTED PDF IS EVER CREATED — an earlier version of this module reassembled the redacted pages into a
 * fresh `PDFFile` (via pdf-lib) purely so the split agent would have a `pdfId` to call `getNumPages()` on and
 * a byte-level fallback to cut if its own page-image-id count ever failed to match. That PDF was pure waste:
 * the split agent's normal path never reads a pdf's bytes at all once it has `redactedImageIds` directly (see
 * `split-file-agent.ts`'s own header), so the only thing that "fallback" was ever a fallback FOR was a
 * self-inflicted mismatch — a bug to fix, not a legitimate input shape to degrade gracefully for. This module's
 * own ORIGINAL file id is reused as the stable identifier threaded downstream instead (see `redactedFileIds`),
 * and `resolveFileIdsToFiles` is taught (via its own `virtualPdfIds` param) to build a byte-free stub `File` for
 * any id present in `redactedImageIds`, rather than reading real PDF bytes back off disk just to construct an
 * object whose bytes are never actually used.
 *
 * Three phases, run in this order — see the module's own functions for why each is shaped the way it is:
 *
 * A. LOCATE ONLY, one model call PER PAGE, run in parallel across every page of every file in the batch, on a
 *    DOWNSCALED copy of the page (see `locateNames`'s own doc-comment for the resolution tradeoff) — finding
 *    a name's bounding box is a coarser judgment than reading it, and this step never transcribes anything at
 *    all; the actual reading happens once, in phase B. It finds EVERY place that identifies the student — the
 *    name field, the name repeated in a header, signatures (also a parent's), the name inside an answer — and
 *    marks one name field per page as the PRIMARY one, the only one phase B reads (see `LOCATE_NAME_PROMPT` and
 *    `name-boxes.ts`). Nothing is drawn yet either, on purpose — redaction needs the box geometry, which phase
 *    A already has, but there is no reason to do the image work twice when phase B might still revise the
 *    canonical reading. A page with nothing that identifies the student is left completely untouched — nothing
 *    to redact, and nothing for the later phases to do with it.
 * B. RECONCILE, ONE VISUAL model call over every page phase A found A PRIMARY name field on (never the others — see
 *    `reconcileNamedPages`'s own doc-comment for why), using a full-resolution crop of each — THIS is where a
 *    name is actually read, for the first and only time. Groups pages that are the same student (by
 *    handwriting) and settles on one clean canonical name per group. There is no placeholder id any more —
 *    see the next paragraph for why — only a `transcribedName` per page.
 * C. REDACT, pure image work (no model call): blackens every box phase A found on a page — plain rects, nothing stamped
 *    on it, so there's no label-fitting/padding tradeoff to make any more (see `redactNamedPage`, now just a
 *    rect draw). Each final page (redacted or, for a nameless page, untouched) is persisted as its OWN
 *    `ImageFile`, keyed by the input file's ORIGINAL id in `redactedImageIds` — no PDF is ever (re)created
 *    from these pages at all; see this header's own note above.
 *
 * NO PLACEHOLDER ID, STAMPED OR OTHERWISE — an earlier version of this module invented one ("Student1", ...)
 * so the split agent would have a VISUAL hint for page-to-student boundaries. That's gone because the box no
 * longer carries any text to read it off of. A model OCR-ing a tiny stamped label off an image was always the
 * wrong mechanism anyway — but the replacement is NOT "tell the model the already-known transcribed name as
 * plain text" either: that would send the real name to the split model's own prompt, which is exactly the
 * leak this whole module exists to prevent (the split model is reached the same way as this module's own
 * phase A/B calls — see this header's own note on OpenRouter). Instead, `split-file-agent.ts`'s own
 * `groupPdfPagesByStudent` derives an ANONYMIZED per-pdf label from `nameMapping` (`"Name 1"`, `"Name 2"`, ...
 * — same real name always gets the same label, different real names always get different labels) and sends
 * only THAT as the per-page text hint, never the name itself. The model decides for itself — using the FULL
 * page content it already has (every page's own handwritten answers, which this module never sees) — whether
 * two page-ranges sharing the same label are the same student or a genuine name collision between two
 * different ones who happen to share a real name. The real name is matched back afterward, entirely on our own
 * side, once the model's grouping is known — see `attachNamesFromMapping` in that file.
 *
 * A page with no name found gets no `transcribedName` entry at all. Clustering a nameless page by
 * page-adjacency is deliberately NOT this module's job — the split agent already decides page-to-student
 * grouping using the full page content, and doing it twice with two different, potentially disagreeing methods
 * would only create a new class of inconsistency. This module only ever reports what it is actually confident
 * of: a page's own, independently read name.
 */

const ANON_MODEL = 'qwen/qwen3.8-27b';
const ANON_REASONING_OVERRIDE: any = { reasoning: { enabled: false } };

export interface PageNameMappingEntry {
  /** The input file's ORIGINAL id — no redacted PDF ever exists to have an id of its own (see this module's
   * own header). Safe to key by even after that original is deleted post-redaction (see
   * `anonymize-submission-pages.job.ts`): this is only ever used downstream as an opaque map key, never
   * dereferenced back into a real document. */
  fileId: string;
  /** 1-based, matching the redacted page order (== the original file's own page order). */
  pageNumber: number;
  /** The reconciled, canonical real name for this page — null when no name was found on it, or when
   * reconciliation never confirmed one (see `reconcileNamedPages`'s own fallback). No placeholder id
   * accompanies this any more; see this module's own header for why. */
  transcribedName: string | null;
}

export interface AnonymizeSubmissionFilesResult {
  /** Always EQUAL to the input `fileIds`, same order — there is no separate "redacted id" any more (see this
   * module's own header: no redacted PDF is ever created). Kept as its own field/name rather than collapsing
   * callers onto `fileIds` directly, so call sites stay explicit about which id they mean. */
  redactedFileIds: string[];
  /** The ALREADY-PERSISTED redacted page image ids, in page order, keyed by the input file's OWN ORIGINAL id
   * (== its entry in `redactedFileIds`, now that they're the same value) — i.e. `redactedImageIds[fileId]`.
   * This is what lets the split agent skip ever resolving real PDF bytes for this file at all — see
   * `resolveFileIdsToFiles`'s own `virtualPdfIds` param. An uploaded image is keyed the same way, as a one-page
   * entry. Absent only for a file that is neither PDF nor image (nothing was ever redacted there, so nothing to
   * key). */
  redactedImageIds: Record<string, string[]>;
  nameMapping: PageNameMappingEntry[];
}

async function loadImageBuffer(imageId: string): Promise<{ buffer: Buffer; width: number; height: number }> {
  const imageRaw = await ImageFile.getByID(imageId)(ImageFile.allFields);
  if (!imageRaw) throw new Error(`ImageFile not found: ${imageId}`);
  const image = imageRaw instanceof ImageFile ? imageRaw : new ImageFile(imageRaw);
  const buffer = await readFile(image.getFullPath());
  const metadata = await sharp(buffer).metadata();
  if (!metadata.width || !metadata.height) throw new Error(`Could not read dimensions for image: ${imageId}`);
  return { buffer, width: metadata.width, height: metadata.height };
}

const LOCATE_NAME_PROMPT = `You see one page from a scanned student exam submission. Find EVERY place on this page that identifies
the student, so each can be blacked out:
- "name": the student's name — usually a printed "Name:" (or similar) label followed by handwritten text,
  sometimes just a handwritten name near the top, sometimes repeated in a header or footer on every page
- "signature": any signature — the student's, a parent's or guardian's ("Unterschrift"), or anyone else's
- "mention": the student's name anywhere else on the page, e.g. inside an answer
Do not confuse these with the school name, exam title, subject, task numbers, or a date.

When you are unsure whether something identifies the student, INCLUDE it: a box too many only hides a few
words, a box too few leaves a name visible.

Each box covers ONLY the name text or signature itself (not a "Name:" label), as tightly as possible. Do NOT
transcribe or repeat any name anywhere in your answer — only locations are needed here; another step reads the
name separately.

Mark exactly one "name" box as "primary": the clearest name field on the page. If there is no "name" box, mark
none. "confidence" (0-1) is how sure you are that a box identifies the student.

Most pages of a multi-page submission show nothing that identifies the student — then answer with an empty
list; that is a normal, expected answer.

Coordinates: 0-1000 normalized (origin top-left, x right, y down).

STRICT JSON only:
{ "boxes": [{ "kind": "name", "primary": true, "confidence": 0.9, "bbox": {"x1":0,"y1":0,"x2":0,"y2":0} }] }
or
{ "boxes": [] }`;

/** One call per page: locate the boxes only — never transcribes a name (see the prompt above: the accurate
 * reading happens later, in `reconcileNamedPages`'s own full-res crop-based call). Never draws anything either —
 * that's `redactNamedPage`. */
const LOCATE_MAX_ATTEMPTS = 3;

// Long-edge cap for the LOCATE call's own image, well below a scanned page's native size (a 300 DPI A4 page
// is roughly 2500px on the long edge). Locating just needs to tell "this looks like a name field" apart from
// the rest of the page — a coarser, structural judgment than actually READING the name, which this function's
// own transcription is only ever a noisy hint for anyway (see `reconcileNamedPages`'s own doc-comment: the
// RECONCILE call re-reads the name from a full-res crop of the ORIGINAL page and that canonical reading is
// what actually survives into `nameMapping`). The bbox this call returns is 0-1000 NORMALIZED, so downscaling
// here needs no coordinate rescaling anywhere else — the same box maps directly onto the full-res page.
const LOCATE_MAX_DIMENSION = 1000;

async function downscaleForLocate(buffer: Buffer): Promise<Buffer> {
  return sharp(buffer)
    .resize(LOCATE_MAX_DIMENSION, LOCATE_MAX_DIMENSION, { fit: 'inside', withoutEnlargement: true })
    .png()
    .toBuffer();
}

/**
 * One page, up to `LOCATE_MAX_ATTEMPTS` tries. An empty `boxes` list is a valid, expected answer and returns
 * immediately, never retried. Everything else that isn't a clean list of usable boxes (see `parseNameBoxes`) — a
 * network/API error, unparseable JSON, a box that cannot be parsed, or one too large to be a name — is treated as
 * a FAILED attempt and retried; if every attempt fails, this throws rather than falling back to "no name found".
 *
 * That fail-CLOSED default matters here specifically: this function exists to keep a name off the page a
 * third-party model later sees, so a response this code can't make sense of must not be silently treated as
 * "nothing to redact" — an earlier version of this function did exactly that (a JSON-parse failure or an
 * invalid bbox both fell through to "no name found", i.e. fail-OPEN), which is the wrong default for a
 * privacy control. A page whose name genuinely can't be confirmed absent should stay in retry/job-failure,
 * never quietly pass through unredacted.
 */
async function locateNames(buffer: Buffer): Promise<LocatedNames & { cost: number }> {
  let totalCost = 0;
  let lastError: unknown;
  const downscaled = await downscaleForLocate(buffer);

  for (let attempt = 1; attempt <= LOCATE_MAX_ATTEMPTS; attempt++) {
    try {
      const dataUrl = `data:image/png;base64,${downscaled.toString('base64')}`;
      const router = getLLMRouter();
      const response = await router.chat.completions.create({
        model: ANON_MODEL,
        provider: 'openrouter',
        temperature: 0,
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: LOCATE_NAME_PROMPT },
          { role: 'user', content: [{ type: 'image_url', image_url: { url: dataUrl, detail: 'low' } }] },
        ],
        ...ANON_REASONING_OVERRIDE,
      });
      totalCost += Number((response.usage as any)?.cost ?? 0);

      const content = response.choices?.[0]?.message?.content;
      const raw = typeof content === 'string' ? content : JSON.stringify(content ?? '');
      return { ...parseNameBoxes(JSON.parse(raw)), cost: totalCost };
    } catch (error) {
      lastError = error;
      logger.warn(
        { err: error, attempt, maxAttempts: LOCATE_MAX_ATTEMPTS, context: 'AnonymizeSubmission' },
        'Name-location attempt failed',
      );
    }
  }

  throw new Error(
    `Name location failed after ${LOCATE_MAX_ATTEMPTS} attempts: ${lastError instanceof Error ? lastError.message : String(lastError)}`,
  );
}

function boxToPixelRect(box: Box, pageWidth: number, pageHeight: number): { left: number; top: number; width: number; height: number } {
  const left = (box.x1 / 1000) * pageWidth;
  const top = (box.y1 / 1000) * pageHeight;
  const width = ((box.x2 - box.x1) / 1000) * pageWidth;
  const height = ((box.y2 - box.y1) / 1000) * pageHeight;
  return { left, top, width, height };
}

const BOX_PAD_FRACTION = 0.08; // margin around the model's own bbox, as a fraction of its own size
const BOX_MIN_PAD_PX = 2;

/**
 * Pads the model's own bbox by a SMALL fraction of its own size (a real redaction-quality improvement on its
 * own: a too-tight bbox risks a sliver of real handwriting surviving right at its edge) and NEVER grows it
 * further than that — the box's own size is a proxy for "how much of the page got redacted," and it must track
 * the actual detection, nothing else. No label-fitting concern any more: the box carries no text (see this
 * module's own header for why), so there is nothing here to size around beyond the detection itself.
 */
function computeRedactionRect(box: Box, pageWidth: number, pageHeight: number): { left: number; top: number; width: number; height: number } {
  const padX = Math.max(BOX_MIN_PAD_PX, ((box.x2 - box.x1) / 1000) * pageWidth * BOX_PAD_FRACTION);
  const padY = Math.max(BOX_MIN_PAD_PX, ((box.y2 - box.y1) / 1000) * pageHeight * BOX_PAD_FRACTION);
  const raw = boxToPixelRect(box, pageWidth, pageHeight);
  return {
    left: Math.max(0, raw.left - padX),
    top: Math.max(0, raw.top - padY),
    width: Math.min(raw.width + padX * 2, pageWidth - Math.max(0, raw.left - padX)),
    height: Math.min(raw.height + padY * 2, pageHeight - Math.max(0, raw.top - padY)),
  };
}

/** Blackens every box (each padded per `computeRedactionRect`) — plain rects, nothing stamped on them. Own
 * `sharp()` pipeline, never chained with a subsequent `.resize()` in the same call — composited-then-resized in
 * one pipeline has been observed to corrupt sharp's own dimension check elsewhere in this codebase; there is no
 * resize here at all, on purpose. */
async function redactNamedPage(buffer: Buffer, boxes: Box[], pageWidth: number, pageHeight: number): Promise<Buffer> {
  const rects = boxes.map(box => computeRedactionRect(box, pageWidth, pageHeight));
  const svg = `<svg width="${pageWidth}" height="${pageHeight}" xmlns="http://www.w3.org/2000/svg">
    ${rects.map(rect => `<rect x="${rect.left}" y="${rect.top}" width="${rect.width}" height="${rect.height}" fill="black" />`).join('\n    ')}
  </svg>`;
  return sharp(buffer).composite([{ input: Buffer.from(svg), top: 0, left: 0 }]).png().toBuffer();
}

const CROP_CONTEXT_PAD_FRACTION = 0.6; // generous — this crop is transient (never persisted, never redacted),
// only used for phase B's own visual comparison, so there's no size-inflation concern like the redaction box has

/** Crops the name region out of the ORIGINAL (unredacted) page, with generous surrounding context, for phase
 * B's visual comparison — never persisted, never shown to anything but that one call. */
async function cropNameRegion(buffer: Buffer, box: Box, pageWidth: number, pageHeight: number): Promise<Buffer> {
  const padX = ((box.x2 - box.x1) / 1000) * pageWidth * CROP_CONTEXT_PAD_FRACTION;
  const padY = ((box.y2 - box.y1) / 1000) * pageHeight * CROP_CONTEXT_PAD_FRACTION;
  const raw = boxToPixelRect(box, pageWidth, pageHeight);
  const left = Math.max(0, Math.round(raw.left - padX));
  const top = Math.max(0, Math.round(raw.top - padY));
  const width = Math.max(1, Math.min(pageWidth - left, Math.round(raw.width + padX * 2)));
  const height = Math.max(1, Math.min(pageHeight - top, Math.round(raw.height + padY * 2)));
  return sharp(buffer).extract({ left, top, width, height }).png().toBuffer();
}

interface NamedPageForReconcile {
  /** Opaque key this module round-trips — not meaningful to the model beyond identifying its own answer. */
  key: string;
  pageNumber: number;
  cropDataUrl: string;
}

const RECONCILE_PROMPT = `Below are cropped images, one per page, each showing a name handwritten on that page — the surrounding
crop gives you context, but the name itself is what matters. Each image is preceded by that page's number.
Read the name directly from the image yourself — nothing upstream has transcribed it, so this is the only
place the actual text comes from.

Group these pages by STUDENT: pages showing the SAME handwritten name get the SAME group, even if your own
readings of the noisier ones differ slightly, as long as the handwriting clearly reads as the same underlying
name. Different students get different groups. Page proximity (close or consecutive page numbers) is
supporting evidence when two images are ambiguous — but the handwriting itself is the primary signal, not page
order alone.

For each group, give exactly ONE canonical name: the clearest reading among its pages' images. Every page
listed below MUST end up in exactly one group. Group order does not matter — only the name and which pages
belong together.

STRICT JSON only:
{ "groups": [{ "canonicalName": "string", "keys": ["string", ...] }] }`;

const RECONCILE_MAX_ATTEMPTS = 3;

/**
 * One attempt: call + parse. Throws on a network/API error, unparseable JSON, or a response with no usable
 * groups at all — anything short of that is a FAILED attempt for `reconcileNamedPages` below to retry.
 *
 * Returns `key -> canonicalName` directly — no placeholder id of any kind any more (see this module's own
 * header: the box isn't labeled, and nothing downstream reads an id, only `transcribedName`). An earlier
 * version self-assigned "Student1", "Student2", ... after an even earlier version let the MODEL invent that id,
 * which was a real, live bug: a stray whitespace-only id from the model passed every truthiness check (a
 * non-empty string is still truthy) and got stamped straight into the redaction box's SVG `<text>`, rendering
 * as a correctly-sized, correctly-colored, completely BLANK box. Dropping the id concept entirely — rather than
 * just moving who assigns it — removes that whole bug class at the root: there is no id left for anything to
 * get wrong.
 */
async function reconcileNamedPagesOnce(pages: NamedPageForReconcile[], examId: string): Promise<Map<string, string>> {
  const router = getLLMRouter();
  const response = await router.chat.completions.create({
    model: ANON_MODEL,
    provider: 'openrouter',
    temperature: 0,
    response_format: { type: 'json_object' },
    messages: [
      { role: 'system', content: RECONCILE_PROMPT },
      {
        role: 'user',
        content: pages.flatMap(p => [
          { type: 'text', text: `key: ${p.key}, page: ${p.pageNumber}` },
          { type: 'image_url', image_url: { url: p.cropDataUrl, detail: 'low' } },
        ]),
      },
    ],
    ...ANON_REASONING_OVERRIDE,
  });

  await addCostToExam({ usage: response.usage }, examId, 'anonymizeSubmissionPages-reconcile').catch(() => {});

  const content = response.choices?.[0]?.message?.content;
  const raw = typeof content === 'string' ? content : JSON.stringify(content ?? '');
  const parsed = JSON.parse(raw);
  const groups = Array.isArray(parsed?.groups) ? parsed.groups : [];
  if (groups.length === 0) throw new Error(`no usable "groups" in response: ${raw}`);

  const knownKeys = new Set(pages.map(p => p.key));

  // Keep only groups with a real name and at least one key that matches a page we actually sent (drop a
  // hallucinated key rather than trust it). No ordering concern any more — there's no id to assign in any
  // particular sequence, just a name per key.
  const result = new Map<string, string>();
  for (const group of groups) {
    const canonicalName = typeof group?.canonicalName === 'string' ? group.canonicalName.trim() : '';
    const keys = Array.isArray(group?.keys) ? group.keys : [];
    if (!canonicalName) continue;
    for (const key of keys) {
      if (typeof key === 'string' && knownKeys.has(key)) result.set(key, canonicalName);
    }
  }
  return result;
}

/**
 * ONE call, over pages phase A found a PRIMARY name field on — never the others. This is
 * reconciliation of transcription noise among CONFIRMED name sightings, not clustering/gap-filling in
 * general; a nameless page is never given an entry here, by design (see this module's own header).
 *
 * VISUAL, not text-only: an earlier version compared phase A's transcribed STRINGS to each other, which threw
 * off the split agent by grouping pages incorrectly — text-only comparison of already-noisy OCR readings
 * throws away exactly the information (actual letterforms, handwriting style) a person would use to recognize
 * two messy transcriptions as the same name. Phase A no longer transcribes anything at all now (see
 * `locateNames`) — this call is the ONLY place a name is ever actually read, straight from a full-res crop.
 *
 * Retries up to `RECONCILE_MAX_ATTEMPTS` times on a failed attempt before falling back to giving every
 * still-unmatched page an EMPTY canonical name. Unlike `locateNames`, that fallback is acceptable even after
 * retries are exhausted: redaction only ever depended on `locateNames`'s own boxes (phase C blackens a page
 * regardless of whether reconcile ever names it — see phase C's own code), so the worst case here is a page
 * contributing no name to match-back (the same as if no name had ever been found on it — see
 * `attachNamesFromMapping` in `split-file-agent.ts`, which already ignores an empty/missing transcription),
 * never an unredacted name.
 */
async function reconcileNamedPages(pages: NamedPageForReconcile[], examId: string): Promise<Map<string, string>> {
  if (pages.length === 0) return new Map();

  let result = new Map<string, string>();
  for (let attempt = 1; attempt <= RECONCILE_MAX_ATTEMPTS; attempt++) {
    try {
      result = await reconcileNamedPagesOnce(pages, examId);
      break;
    } catch (error) {
      logger.warn(
        { err: error, attempt, maxAttempts: RECONCILE_MAX_ATTEMPTS, context: 'AnonymizeSubmission' },
        'Reconcile attempt failed',
      );
    }
  }

  // Anything the model failed to place (including every attempt failing outright) still needs an entry — its
  // ABSENCE, not an empty name, is what phase C refuses as a broken guarantee (see phase C's own `page.primary`
  // check); a named page must always end up with SOME entry here so it's still redacted.
  for (const page of pages) {
    if (!result.has(page.key)) result.set(page.key, '');
  }

  logger.info(
    { pages: pages.length, named: Array.from(result.values()).filter(Boolean).length, context: 'AnonymizeSubmission' },
    'Reconciled named pages',
  );
  return result;
}

async function removeUnredactedPageImages(imageIds: string[], examId: string): Promise<void> {
  await Promise.all(
    imageIds.map(imageId =>
      ImageFile.removeFile(imageId)().catch(err =>
        logger.error({ err, examId, imageId, context: 'AnonymizeSubmission' }, 'Could not delete unredacted page image'),
      ),
    ),
  );
}

interface PageWork {
  fileIndex: number;
  pageIndex: number;
  imageId: string;
}

export async function anonymizeSubmissionFiles(fileIds: string[], examId: string): Promise<AnonymizeSubmissionFilesResult> {
  if (fileIds.length === 0) return { redactedFileIds: [], redactedImageIds: {}, nameMapping: [] };

  const sourceFiles = await resolveFileIdsToFiles(fileIds);

  // An image upload is redacted too — OCR and grading see it just like a PDF page. It is its own single page:
  // nothing to rasterise, and its redacted page goes into `redactedImageIds` like any PDF's, which is what sends
  // it down the split agent's single-page path (no model call, name straight from `nameMapping`).
  const pdfFileIndexes: number[] = [];
  const perFilePdfId: (string | null)[] = [];
  const perFileUploadedImageId: (string | null)[] = [];
  for (let i = 0; i < sourceFiles.length; i++) {
    const file = sourceFiles[i]!;
    const sourceImageId = typeof (file as any)?.sourceImageId === 'string' ? (file as any).sourceImageId : undefined;
    perFileUploadedImageId.push(sourceImageId ?? null);
    if (file.type !== 'application/pdf') {
      perFilePdfId.push(null);
      continue;
    }
    const sourcePdfId = typeof (file as any)?.sourcePdfId === 'string' ? (file as any).sourcePdfId : undefined;
    const pdfId = sourcePdfId || (await PDFFile.upload(file));
    perFilePdfId.push(pdfId);
    pdfFileIndexes.push(i);
  }

  const perFileImageIds: string[][] = await Promise.all(
    perFilePdfId.map((pdfId, i) => {
      if (pdfId) return extractImageFromPDF(pdfId);
      const uploadedImageId = perFileUploadedImageId[i];
      return Promise.resolve(uploadedImageId ? [uploadedImageId] : []);
    }),
  );

  // The pages rasterised above are the UNREDACTED originals, persisted as ImageFiles only because that is how
  // `extractImageFromPDF` hands them over. Nothing downstream may ever see them (see this module's header), so
  // they are deleted once this function is done with them — in `finally`, so a failed or retried redaction does
  // not leave a real-name copy behind either. An uploaded image's page IS the upload itself: it is not deleted
  // here (a failed attempt would lose it for the retry) but by the job, once redaction has succeeded.
  const rasterisedPageIds = perFileImageIds.filter((_, i) => perFilePdfId[i]).flat();
  try {
    const allPages: PageWork[] = perFileImageIds.flatMap((imageIds, fileIndex) =>
      imageIds.map((imageId, pageIndex) => ({ fileIndex, pageIndex, imageId })),
    );

    // PHASE A — parallel, per page, across every file in the batch. Locates boxes ONLY, on a downscaled copy of
    // the page — see this module's header for why nothing is drawn until phase C, and `locateNames`'s own
    // doc-comment for why this step never transcribes anything.
    logger.info({ pages: allPages.length, context: 'AnonymizeSubmission' }, 'Locating names across all pages');
    let phaseACost = 0;
    const phaseA = await Promise.all(
      allPages.map(async page => {
        const { buffer, width, height } = await loadImageBuffer(page.imageId);
        const { boxes, primary, cost } = await locateNames(buffer);
        phaseACost += cost;
        return { ...page, width, height, buffer, boxes, primary };
      }),
    );
    await addCostToExam({ usage: { cost: phaseACost } as any }, examId, 'anonymizeSubmissionPages-locate').catch(() => {});

    // PHASE B — one call, visual, over every page phase A found a PRIMARY name field on (name text itself is read
    // HERE, for the first and only time — see `reconcileNamedPages`'s own doc-comment). Only that one box per page
    // is read: further boxes (header, signature, mention) are blackened, never read, so the name still reaches
    // `nameMapping` once per page. Crops are taken from the ORIGINAL (unredacted), FULL-RES buffer — this is the
    // one place besides phase A's own bbox call that sees the real name, and only as a small, transient,
    // never-persisted crop for this one comparison call.
    const namedPages: NamedPageForReconcile[] = await Promise.all(
      phaseA
        .filter(p => p.primary)
        .map(async p => ({
          key: `${p.fileIndex}:${p.pageIndex}`,
          pageNumber: p.pageIndex + 1,
          cropDataUrl: `data:image/png;base64,${(await cropNameRegion(p.buffer, p.primary!, p.width, p.height)).toString('base64')}`,
        })),
    );
    const clusters = await reconcileNamedPages(namedPages, examId);

    // PHASE C — parallel, pure image work: blacken EVERY box phase A found on a page (see `redactNamedPage`); a
    // page without any is returned exactly as loaded, untouched. A page WITH a primary box but no reconcile entry
    // THROWS rather than passing the real, unredacted page through — `reconcileNamedPages` guarantees every page
    // it was given an entry for (even an empty-name one), so this should be unreachable; if it's ever hit, that
    // guarantee has broken, and this is a privacy control, so the fail-closed default is to stop the job, not ship
    // an unredacted page (see `locateNames`'s own doc-comment on why this pipeline fails closed throughout).
    const phaseC = await Promise.all(
      phaseA.map(async page => {
        if (page.boxes.length === 0) return page.buffer;
        const key = `${page.fileIndex}:${page.pageIndex}`;
        if (page.primary && !clusters.has(key)) {
          throw new Error(`Page ${key} had a detected name box but no reconcile entry — refusing to ship it unredacted`);
        }
        return redactNamedPage(page.buffer, page.boxes, page.width, page.height);
      }),
    );

    // Persist each final page as its OWN ImageFile, keyed by this file's ORIGINAL id — no PDF is reassembled at
    // all any more (see this module's own header for why that PDF was pure waste).
    const redactedFileIds: string[] = fileIds.slice();
    const redactedImageIds: Record<string, string[]> = {};
    const nameMapping: PageNameMappingEntry[] = [];

    for (let fileIndex = 0; fileIndex < sourceFiles.length; fileIndex++) {
      const sourceId = perFilePdfId[fileIndex] ?? perFileUploadedImageId[fileIndex];
      if (!sourceId) continue; // neither PDF nor image — passthrough, nothing redacted, nothing to key

      const originalFileId = fileIds[fileIndex]!;
      const pagesForFile = phaseA
        .map((p, globalIndex) => ({ ...p, globalIndex }))
        .filter(p => p.fileIndex === fileIndex)
        .sort((a, b) => a.pageIndex - b.pageIndex);
      const pageBuffers = pagesForFile.map(p => phaseC[p.globalIndex]!);

      const imageIdsForFile = await Promise.all(
        pageBuffers.map(async (pageBuffer, pageIndex) => {
          const imageFile = await ImageFile.createFromBuffer(pageBuffer, `anonymized-${sourceId}-page-${pageIndex}.png`);
          await imageFile.save();
          return String(imageFile._id);
        }),
      );
      redactedImageIds[originalFileId] = imageIdsForFile;

      for (const page of pagesForFile) {
        const canonicalName = page.primary ? clusters.get(`${page.fileIndex}:${page.pageIndex}`) : undefined;
        nameMapping.push({
          fileId: originalFileId,
          pageNumber: page.pageIndex + 1,
          transcribedName: canonicalName || null,
        });
      }
    }

    logger.info(
      {
        files: sourceFiles.length,
        pages: allPages.length,
        namedPages: namedPages.length,
        redactedBoxes: phaseA.reduce((sum, page) => sum + page.boxes.length, 0),
        context: 'AnonymizeSubmission',
      },
      'Anonymization finished',
    );
    return { redactedFileIds, redactedImageIds, nameMapping };
  } finally {
    await removeUnredactedPageImages(rasterisedPageIds, examId);
  }
}
